// Compliance > Sent to ERP filters (PD-17390).
//
// The list is served by crm-api's LicenseExportLogService
// (GET /api/license-export-log), which reads these filters by key off the
// request's `filters` payload and applies them in SQL — not through
// applyFiltersToQueryBuilder, hence `joinType: 'custom'`. Option ids are the
// values the log stores (results, ERP names) or the server's status codes;
// none parses as a number, so they survive the list's URL state.

import {
	LICENSE_EXPORT_ERPS,
	LICENSE_EXPORT_LOG_STATUSES,
	LICENSE_EXPORT_RESULTS,
} from '@supply-mover-2-0/shared';
import { createDateRangeFilter, createKeywordFilter } from './factories';
import type { FilterConfig, FilterRegistry } from './types';

export const LICENSE_EXPORT_LOG_FILTER_LIST_KEY = 'license_export_log';
const LIST_KEYS: Array<string> = [LICENSE_EXPORT_LOG_FILTER_LIST_KEY];
const OBJECT_TABLE = 'license_export_log';

const custom = {
	objectTable: OBJECT_TABLE,
	joinType: 'custom' as const,
};

const keywordFilter: FilterConfig = {
	...createKeywordFilter({
		label: 'Search',
		placeholder: 'Account, customer #, license # or ship-to...',
		objectTable: OBJECT_TABLE,
		listKeys: LIST_KEYS,
	}),
	queryParamName: 'exportLogKeywords',
	queryMapping: custom,
};

const runDateFilter: FilterConfig = {
	...createDateRangeFilter({
		label: 'Sent',
		objectTable: OBJECT_TABLE,
		dateField: 'runStartedAt',
		listKeys: LIST_KEYS,
	}),
	queryParamName: 'exportLogRunDate',
	queryMapping: { ...custom, dateField: 'runStartedAt' },
};

export const licenseExportLogFilters: FilterRegistry = {
	exportLogKeywords: keywordFilter,

	// The Unacknowledged tab locks (and hides) it; on All it is a filter.
	exportLogStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'exportLogStatus',
		multiple: true,
		valueExtractor: { property: 'id' },
		options: LICENSE_EXPORT_LOG_STATUSES.map((status) => ({
			id: status.id,
			label: status.label,
		})),
		queryMapping: custom,
		format: 'array',
		listKeys: LIST_KEYS,
	},

	exportLogRunDate: runDateFilter,

	exportLogErp: {
		type: 'multi-select',
		label: 'ERP',
		placeholder: 'Select ERP...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'exportLogErp',
		multiple: true,
		valueExtractor: { property: 'id' },
		options: LICENSE_EXPORT_ERPS.map((erp) => ({
			id: erp.id,
			label: erp.label,
		})),
		queryMapping: custom,
		format: 'array',
		listKeys: LIST_KEYS,
	},

	exportLogResult: {
		type: 'multi-select',
		label: 'Result',
		placeholder: 'Select result...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'exportLogResult',
		multiple: true,
		valueExtractor: { property: 'id' },
		options: LICENSE_EXPORT_RESULTS.map((result) => ({
			id: result.id,
			label: result.label,
		})),
		queryMapping: custom,
		format: 'array',
		listKeys: LIST_KEYS,
	},
};
